import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import db, { storage } from "../Firebase";
import { useAuth } from "../AuthContext";
import { THEMES, FREE_THEMES, CardRenderer, CARD_FONTS } from "../CardThemes";
import UpgradeModal from "./UpgradeModal";

const SOCIAL_PLATFORMS = [
    { key: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/in/yourname", color: "#0A66C2", icon: "in" },
    { key: "twitter", label: "Twitter / X", placeholder: "https://twitter.com/yourhandle", color: "#000000", icon: "𝕏" },
    { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/yourname", color: "#E1306C", icon: "IG" },
    { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/yourname", color: "#1877F2", icon: "f" },
    { key: "github", label: "GitHub", placeholder: "https://github.com/yourname", color: "#333333", icon: "GH" },
    { key: "whatsapp", label: "WhatsApp", placeholder: "+91 98765 43210", color: "#25D366", icon: "W" },
    { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@channel", color: "#FF0000", icon: "▶" },
    { key: "behance", label: "Behance", placeholder: "https://behance.net/yourname", color: "#1769FF", icon: "BE" },
    { key: "dribbble", label: "Dribbble", placeholder: "https://dribbble.com/yourname", color: "#EA4C89", icon: "DR" },
    { key: "custom", label: "Custom Link", placeholder: "https://yourlink.com", color: "#6366f1", icon: "+" },
];

const blankSocials = Object.fromEntries(
    SOCIAL_PLATFORMS.map((p) => [p.key, p.key === "custom" ? { enabled: false, url: "", label: "" } : { enabled: false, url: "" }])
);

const STEPS = ["Basic Info", "Professional", "Links & Social"];

export default function EditCard() {
    const { id } = useParams();
    const navigate = useNavigate();
    const routerLocation = useLocation();

    const { user, isPremium, premiumPlan, premiumExpiry } = useAuth();
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [docId, setDocId] = useState(null);
    const [cardOwnerId, setCardOwnerId] = useState(null);
    const [fetching, setFetching] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [step, setStep] = useState(0);
    const [mobileTab, setMobileTab] = useState("form");
    const [theme, setTheme] = useState(routerLocation.state?.preselectedTheme || "dark");
    const [slug, setSlug] = useState("");
    const [originalSlug, setOriginalSlug] = useState("");
    const [slugStatus, setSlugStatus] = useState("available");
    const slugTimer = useRef(null);

    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [location, setLocation] = useState("");
    const [designation, setDesignation] = useState("");
    const [company, setCompany] = useState("");
    const [bio, setBio] = useState("");
    const [websiteEnabled, setWebsiteEnabled] = useState(false);
    const [website, setWebsite] = useState("");
    const [socials, setSocials] = useState(blankSocials);

    const [existingPhotoUrl, setExistingPhotoUrl] = useState("");
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [cropSrc, setCropSrc] = useState(null);
    const [cropPos, setCropPos] = useState({ x: 0, y: 0 });
    const [cropZoom, setCropZoom] = useState(1);
    const cropImgRef = useRef(null);
    const cropDragRef = useRef(null);

    const [existingBannerUrl, setExistingBannerUrl] = useState("");
    const [bannerFile, setBannerFile] = useState(null);
    const [bannerPreview, setBannerPreview] = useState(null);

    const [existingResumeUrl, setExistingResumeUrl] = useState("");
    const [resumeFile, setResumeFile] = useState(null);
    const [resumeName, setResumeName] = useState("");

    const [promoVideoEnabled, setPromoVideoEnabled] = useState(false);
    const [promoVideoType, setPromoVideoType] = useState("youtube");
    const [promoVideoUrl, setPromoVideoUrl] = useState("");
    const [existingPromoVideoUrl, setExistingPromoVideoUrl] = useState("");
    const [promoVideoFile, setPromoVideoFile] = useState(null);
    const videoRef = useRef(null);
    const [showVideoModal, setShowVideoModal] = useState(false);

    useEffect(() => {
        const fetchCard = async () => {
            try {
                const snapshot = await getDocs(
                    query(collection(db, "cards"), where("slug", "==", id))
                );
                if (snapshot.empty) { setFetching(false); return; }

                const docSnap = snapshot.docs[0];
                const data = docSnap.data();
                setDocId(docSnap.id);
                setCardOwnerId(data.userId || null);
                setSlug(data.slug || id);
                setOriginalSlug(data.slug || id);
                setSlugStatus("available");

                setName(data.name || "");
                setPhone(data.phone || "");
                setEmail(data.email || "");
                setLocation(data.location || "");
                setDesignation(data.designation || "");
                setCompany(data.company || "");
                setBio(data.bio || "");

                if (data.website) { setWebsiteEnabled(true); setWebsite(data.website); }

                const prefilled = { ...blankSocials };
                if (data.socials) {
                    Object.entries(data.socials).forEach(([key, url]) => {
                        if (key === "customLabel") return; // handled separately below
                        if (prefilled[key] !== undefined && url)
                            prefilled[key] = { enabled: true, url };
                    });
                    if (data.socials.custom) {
                        prefilled.custom = { enabled: true, url: data.socials.custom, label: data.socials.customLabel || "" };
                    }
                }
                setSocials(prefilled);

                setExistingPhotoUrl(data.photoUrl || "");
                setExistingBannerUrl(data.bannerUrl || "");
                setExistingResumeUrl(data.resumeUrl || "");
                if (data.resumeUrl) setResumeName("Resume already uploaded");
                if (data.promoVideoUrl) {
                    setExistingPromoVideoUrl(data.promoVideoUrl);
                    setPromoVideoUrl(data.promoVideoUrl);
                    setPromoVideoType(data.promoVideoType || "youtube");
                    setPromoVideoEnabled(true);
                }
                setTheme(routerLocation.state?.preselectedTheme || data.theme || "dark");
            } catch (err) {
                console.error("Error loading card:", err);
            } finally {
                setFetching(false);
            }
        };
        fetchCard();
    }, [id, routerLocation.state]);

    useEffect(() => {
        if (routerLocation.state?.preselectedTheme) {
            setTheme(routerLocation.state.preselectedTheme);
        }
    }, [routerLocation.state]);

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setCropSrc(ev.target.result);
            setCropPos({ x: 0, y: 0 });
            setCropZoom(1);
        };
        reader.readAsDataURL(file);
        e.target.value = "";
    };

    const confirmCrop = () => {
        const img = cropImgRef.current;
        if (!img) return;
        const viewport = 240; // matches the circular crop UI size
        const output = 400;
        const srcX = img.naturalWidth / 2 - viewport / (2 * cropZoom) - cropPos.x / cropZoom;
        const srcY = img.naturalHeight / 2 - viewport / (2 * cropZoom) - cropPos.y / cropZoom;
        const srcW = viewport / cropZoom;
        const srcH = viewport / cropZoom;
        const canvas = document.createElement("canvas");
        canvas.width = output;
        canvas.height = output;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, output, output);
        canvas.toBlob((blob) => {
            const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
            setPhotoFile(file);
            setPhotoPreview(canvas.toDataURL("image/jpeg"));
            setCropSrc(null);
        }, "image/jpeg", 0.92);
    };

    const handleBannerChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setBannerFile(file);
        setBannerPreview(URL.createObjectURL(file));
    };

    const handleResumeChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setResumeFile(file);
        setResumeName(file.name);
    };

    const handleResumeDelete = () => {
        setResumeFile(null);
        setResumeName("");
        setExistingResumeUrl("");
    };

    const toggleSocial = (key) =>
        setSocials(prev => ({ ...prev, [key]: { ...prev[key], enabled: !prev[key].enabled } }));

    const updateSocialUrl = (key, url) =>
        setSocials(prev => ({ ...prev, [key]: { ...prev[key], url } }));

    const updateSocialLabel = (label) =>
        setSocials(prev => ({ ...prev, custom: { ...prev.custom, label } }));

    const checkSlug = (value) => {
        if (!value || value === originalSlug) { setSlugStatus("available"); return; }
        setSlugStatus("checking");
        clearTimeout(slugTimer.current);
        slugTimer.current = setTimeout(async () => {
            const snap = await getDocs(query(collection(db, "cards"), where("slug", "==", value)));
            setSlugStatus(snap.empty ? "available" : "taken");
        }, 500);
    };

    const handleSlugChange = (raw) => {
        const clean = raw.toLowerCase().replace(/[^a-z0-9-]/g, "").replace(/--+/g, "-");
        setSlug(clean);
        checkSlug(clean);
    };

    const handleSubmit = async () => {
        if (!docId) return;
        if (!FREE_THEMES.includes(theme) && !isPremium) {
            setShowUpgrade(true);
            return;
        }
        setLoading(true);
        setError("");
        try {
            let photoUrl = existingPhotoUrl;
            if (photoFile) {
                const photoRef = ref(storage, `profile-photos/${id}_${Date.now()}`);
                await uploadBytes(photoRef, photoFile);
                photoUrl = await getDownloadURL(photoRef);
            }
            let bannerUrl = existingBannerUrl;
            if (bannerFile) {
                const bannerRef = ref(storage, `banners/${id}_${Date.now()}`);
                await uploadBytes(bannerRef, bannerFile);
                bannerUrl = await getDownloadURL(bannerRef);
            }
            let resumeUrl = existingResumeUrl;
            if (resumeFile) {
                const resumeRef = ref(storage, `resumes/${id}_${Date.now()}_${resumeFile.name}`);
                await uploadBytes(resumeRef, resumeFile);
                resumeUrl = await getDownloadURL(resumeRef);
            }
            let finalPromoVideoUrl = "";
            let finalPromoVideoType = "";
            if (promoVideoEnabled) {
                if (promoVideoType === "youtube" && promoVideoUrl.trim()) {
                    finalPromoVideoUrl = promoVideoUrl.trim();
                    finalPromoVideoType = "youtube";
                } else if (promoVideoType === "upload" && promoVideoFile) {
                    const videoStorageRef = ref(storage, `promo-videos/${id}_${Date.now()}_${promoVideoFile.name}`);
                    await uploadBytes(videoStorageRef, promoVideoFile);
                    finalPromoVideoUrl = await getDownloadURL(videoStorageRef);
                    finalPromoVideoType = "upload";
                } else if (promoVideoType === "upload" && existingPromoVideoUrl) {
                    finalPromoVideoUrl = existingPromoVideoUrl;
                    finalPromoVideoType = "upload";
                }
            }
            const socialsToSave = {};
            SOCIAL_PLATFORMS.forEach(({ key }) => {
                if (socials[key].enabled && socials[key].url.trim()) {
                    socialsToSave[key] = socials[key].url.trim();
                    if (key === "custom" && socials.custom.label?.trim())
                        socialsToSave.customLabel = socials.custom.label.trim();
                }
            });
            await updateDoc(doc(db, "cards", docId), {
                slug,
                name: name.trim(), phone: phone.trim(), email: email.trim(),
                location: location.trim(), designation: designation.trim(),
                company: company.trim(), bio: bio.trim(),
                website: websiteEnabled ? website.trim() : "",
                photoUrl, bannerUrl, resumeUrl, socials: socialsToSave,
                theme,
                promoVideoUrl: finalPromoVideoUrl,
                promoVideoType: finalPromoVideoType,
                updatedAt: new Date(),
            });
            navigate(`/card/${slug}`);
        } catch (err) {
            console.error("Error updating card:", err);
            setError(`Failed to update: ${err.code || err.message}`);
            setLoading(false);
        }
    };

    const displayPhoto = photoPreview || existingPhotoUrl;
    const initials = name.trim()
        ? name.trim().split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
        : "?";

    // Auth loading
    if (user === undefined || fetching) return (
        <div style={{ ...s.page, display: "flex", alignItems: "center", justifyContent: "center" }}><p style={{ color: "#fff" }}>Loading...</p></div>
    );

    // Not logged in
    if (!user) return (
        <div style={s.accessDenied}>
            <div style={s.accessBox}>
                <div style={s.accessIcon}>🔒</div>
                <h2 style={s.accessTitle}>Sign in required</h2>
                <p style={s.accessSub}>You need to be signed in to edit a card.</p>
                <button onClick={() => navigate("/")} style={s.accessBtn}>Go Home</button>
            </div>
        </div>
    );

    // Logged in but not the owner
    if (cardOwnerId && user.uid !== cardOwnerId) return (
        <div style={s.accessDenied}>
            <div style={s.accessBox}>
                <div style={s.accessIcon}>⛔</div>
                <h2 style={s.accessTitle}>Access denied</h2>
                <p style={s.accessSub}>This card belongs to someone else. You can only edit your own cards.</p>
                <button onClick={() => navigate("/")} style={s.accessBtn}>Go Home</button>
            </div>
        </div>
    );

    if (!docId) return (
        <div style={s.page}>
            <div style={{ background: "#fff", borderRadius: "16px", padding: "2rem", textAlign: "center" }}>
                <p>Card not found.</p>
                <a href="/" style={{ color: "#0f3460", fontWeight: 700 }}>Go home</a>
            </div>
        </div>
    );

    return (
        <div className="edit-root">
            <style>{`
                .edit-root { min-height: 100vh; display: grid; grid-template-columns: 1fr 420px; font-family: 'Segoe UI', Arial, sans-serif; }
                .edit-form-panel { background: #fff; display: flex; flex-direction: column; border-right: 1px solid #f3f4f6; min-height: 100vh; }
                .edit-preview-panel { background: linear-gradient(160deg, #0f0c29 0%, #1a1a2e 50%, #0f3460 100%); padding: 2rem 1.5rem; display: flex; flex-direction: column; align-items: center; position: sticky; top: 0; height: 100vh; overflow-y: auto; }
                .edit-tab-bar { display: none; }
                @media (max-width: 768px) {
                    .edit-root { display: flex; flex-direction: column; }
                    .edit-form-panel { display: ${mobileTab === "form" ? "flex" : "none"}; flex-direction: column; min-height: calc(100vh - 60px); padding-bottom: 80px; }
                    .edit-preview-panel { display: ${mobileTab === "preview" ? "flex" : "none"}; height: auto; min-height: calc(100vh - 60px); position: static; padding-bottom: 80px; }
                    .edit-tab-bar { display: flex; position: fixed; bottom: 0; left: 0; right: 0; background: #fff; border-top: 1px solid #f3f4f6; z-index: 210; height: 60px; }
                }
            `}</style>

            {/* Left — Form */}
            <div className="edit-form-panel">
                {/* Top bar */}
                <div style={s.topBar}>
                    <button onClick={() => navigate(`/card/${id}`)} style={s.backBtn}>← Back</button>
                    <span style={s.topLogo}><span style={s.logoDot}>●</span> Edit Card</span>
                </div>

                {/* Premium status banner */}
                {isPremium && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 2rem", background: "linear-gradient(135deg, rgba(10,173,161,0.08), rgba(15,143,133,0.05))", borderBottom: "1px solid rgba(10,173,161,0.15)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div style={{ background: "linear-gradient(135deg,#0AADA1,#0f8f85)", borderRadius: "20px", padding: "3px 10px", fontSize: "11px", fontWeight: "800", color: "#fff", letterSpacing: "0.3px" }}>
                                ✨ PREMIUM
                            </div>
                            <span style={{ fontSize: "12px", color: "#0AADA1", fontWeight: "600" }}>
                                {premiumPlan === "yearly" ? "Yearly" : "Monthly"} plan active
                            </span>
                        </div>
                        {premiumExpiry && (
                            <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                                Valid till {new Date(premiumExpiry).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                            </span>
                        )}
                    </div>
                )}

                {/* Step bar */}
                <div style={s.stepBar}>
                    {STEPS.map((label, i) => (
                        <div key={i} style={s.stepItem}>
                            <div style={{
                                ...s.stepCircle,
                                background: i <= step ? "#6366f1" : "#e5e7eb",
                                color: i <= step ? "#fff" : "#9ca3af",
                                boxShadow: i === step ? "0 0 0 4px rgba(99,102,241,0.2)" : "none",
                            }}>
                                {i < step ? "✓" : i + 1}
                            </div>
                            <span style={{ ...s.stepLabel, color: i === step ? "#6366f1" : i < step ? "#1a1a2e" : "#9ca3af", fontWeight: i === step ? "700" : "500" }}>
                                {label}
                            </span>
                            {i < STEPS.length - 1 && (
                                <div style={{ ...s.stepLine, background: i < step ? "#6366f1" : "#e5e7eb" }} />
                            )}
                        </div>
                    ))}
                </div>

                <div style={s.formBody}>
                    <div style={s.stepContent}>

                        {/* STEP 0 */}
                        {step === 0 && (
                            <>
                                <h2 style={s.stepHeading}>Basic Info</h2>
                                <p style={s.stepSub}>Update your core contact details.</p>

                                <div style={s.photoArea}>
                                    <div style={s.avatarWrap}>
                                        {displayPhoto
                                            ? <img src={displayPhoto} alt="preview" style={s.avatarImg} />
                                            : <div style={s.avatarInitials}>{initials}</div>
                                        }
                                        <label htmlFor="photo-input" style={s.cameraBtn}><CameraIcon /></label>
                                        <input id="photo-input" type="file" accept="image/*"
                                            onChange={handlePhotoChange} style={{ display: "none" }} />
                                    </div>
                                    <div>
                                        <p style={s.photoTitle}>Profile Photo</p>
                                        <p style={s.photoHint}>Tap to change your photo</p>
                                        <label htmlFor="photo-input" style={s.photoUploadBtn}>Change Photo</label>
                                    </div>
                                </div>

                                {/* Banner Upload (dark theme only) */}
                                {theme === "dark" && (
                                    <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem", background: "#f9fafb", borderRadius: "14px", padding: "1rem", border: "1.5px dashed #e5e7eb", marginBottom: "1rem" }}>
                                        <div style={{ width: "120px", height: "52px", borderRadius: "8px", background: "#e5e7eb", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            {(bannerPreview || existingBannerUrl)
                                                ? <img src={bannerPreview || existingBannerUrl} alt="banner" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                : <span style={{ fontSize: "0.7rem", color: "#9ca3af" }}>🖼 Cover</span>
                                            }
                                        </div>
                                        <div>
                                            <p style={s.photoTitle}>Cover / Banner Image</p>
                                            <p style={s.photoHint}>Wide image for dark card top (16:7 ratio best)</p>
                                            <label htmlFor="banner-input" style={s.photoUploadBtn}>Change Banner</label>
                                            <input id="banner-input" type="file" accept="image/*"
                                                onChange={handleBannerChange} style={{ display: "none" }} />
                                        </div>
                                    </div>
                                )}

                                <div style={s.fieldGrid}>
                                    <Field label="Full Name *" value={name} onChange={setName} placeholder="Shlok Singh" required span={2} />
                                    <Field label="Phone *" value={phone} onChange={setPhone} placeholder="+91 98765 43210" required />
                                    <Field label="Email *" type="email" value={email} onChange={setEmail} placeholder="shlok@example.com" required />
                                    <Field label="Location" value={location} onChange={setLocation} placeholder="Greater Noida, UP" span={2} />
                                </div>

                                {/* URL Handle */}
                                <div style={s.field}>
                                    <label style={s.label}>Your Card URL</label>
                                    <div style={s.slugWrap}>
                                        <span style={s.slugBase}>myprofilecorner.com/</span>
                                        <input
                                            style={{ ...s.slugInput, borderColor: slugStatus === "available" ? "#22c55e" : slugStatus === "taken" ? "#ef4444" : "#e5e7eb" }}
                                            value={slug}
                                            onChange={(e) => handleSlugChange(e.target.value)}
                                            placeholder="your name"
                                        />
                                    </div>
                                    <div style={s.slugStatus}>
                                        {slugStatus === "checking" && <span style={s.slugChecking}>⏳ Checking availability...</span>}
                                        {slugStatus === "available" && slug === originalSlug && <span style={s.slugAvailable}>✓ Current URL — myprofilecorner.com/{slug}</span>}
                                        {slugStatus === "available" && slug !== originalSlug && <span style={s.slugAvailable}>✓ Available — myprofilecorner.com/{slug}</span>}
                                        {slugStatus === "taken" && <span style={s.slugTaken}>✗ Already taken — try a different handle</span>}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* STEP 1 */}
                        {step === 1 && (
                            <>
                                <h2 style={s.stepHeading}>Professional Info</h2>
                                <p style={s.stepSub}>Update your role, company, and bio.</p>

                                <div style={s.fieldGrid}>
                                    <Field label="Designation / Role" value={designation} onChange={setDesignation} placeholder="Product Designer" span={2} />
                                    <Field label="Company / Organisation" value={company} onChange={setCompany} placeholder="Empower Pragati" span={2} />
                                </div>

                                <div style={s.field}>
                                    <label style={s.label}>Short Bio</label>
                                    <textarea
                                        style={{ ...s.input, resize: "vertical", minHeight: "100px", paddingTop: "0.75rem" }}
                                        placeholder="Tell people about yourself..."
                                        value={bio} onChange={(e) => setBio(e.target.value)} rows={4}
                                    />
                                </div>

                                <div style={s.resumeUploadArea}>
                                    <div style={{ fontSize: "1.5rem" }}>📄</div>
                                    <div style={{ flex: 1 }}>
                                        <p style={s.resumeTitle}>Resume / CV</p>
                                        <p style={s.resumeHint}>{resumeName || "No resume uploaded"}</p>
                                    </div>
                                    <label htmlFor="resume-input" style={s.resumeBtn}>
                                        {resumeName ? "Replace" : "Upload"}
                                    </label>
                                    <input id="resume-input" type="file" accept=".pdf,.doc,.docx"
                                        onChange={handleResumeChange} style={{ display: "none" }} />
                                    {resumeName && (
                                        <button
                                            type="button"
                                            onClick={handleResumeDelete}
                                            style={s.resumeDeleteBtn}
                                            title="Remove resume"
                                        >✕</button>
                                    )}
                                </div>
                            </>
                        )}

                        {/* STEP 2 */}
                        {step === 2 && (
                            <>
                                <h2 style={s.stepHeading}>Links & Social</h2>
                                <p style={s.stepSub}>Toggle on what you want to show.</p>

                                {/* Website */}
                                <div style={s.linkRow}>
                                    <div style={s.linkLeft}>
                                        <div style={{ ...s.linkIcon, background: "#f0f0ff" }}>🌐</div>
                                        <div>
                                            <p style={s.linkLabel}>Website</p>
                                            <p style={s.linkHint}>Your personal or business site</p>
                                        </div>
                                    </div>
                                    <label style={s.toggle}>
                                        <input type="checkbox" checked={websiteEnabled}
                                            onChange={() => setWebsiteEnabled(v => !v)} style={{ display: "none" }} />
                                        <div style={{ ...s.toggleTrack, background: websiteEnabled ? "#6366f1" : "#e5e7eb" }}>
                                            <div style={{ ...s.toggleThumb, left: websiteEnabled ? "22px" : "2px" }} />
                                        </div>
                                    </label>
                                </div>
                                {websiteEnabled && (
                                    <input style={{ ...s.input, marginBottom: "0.5rem" }}
                                        placeholder="https://yourwebsite.com"
                                        value={website} onChange={(e) => setWebsite(e.target.value)} />
                                )}

                                <div style={s.divider} />

                                {SOCIAL_PLATFORMS.map(({ key, label, placeholder, color, icon }) => (
                                    <div key={key}>
                                        <div style={s.linkRow}>
                                            <div style={s.linkLeft}>
                                                <div style={{ ...s.socialBadge, background: color }}>
                                                    <span style={{ color: "#fff", fontSize: "0.7rem", fontWeight: "800" }}>{icon}</span>
                                                </div>
                                                <p style={s.linkLabel}>{label}</p>
                                            </div>
                                            <label style={s.toggle}>
                                                <input type="checkbox" checked={socials[key].enabled}
                                                    onChange={() => toggleSocial(key)} style={{ display: "none" }} />
                                                <div style={{ ...s.toggleTrack, background: socials[key].enabled ? color : "#e5e7eb" }}>
                                                    <div style={{ ...s.toggleThumb, left: socials[key].enabled ? "22px" : "2px" }} />
                                                </div>
                                            </label>
                                        </div>
                                        {socials[key].enabled && (
                                            <>
                                                <input style={{ ...s.input, marginBottom: "0.5rem" }}
                                                    placeholder={placeholder}
                                                    value={socials[key].url}
                                                    onChange={(e) => updateSocialUrl(key, e.target.value)} />
                                                {key === "custom" && (
                                                    <input style={{ ...s.input, marginBottom: "0.5rem" }}
                                                        placeholder="Link name (e.g. Portfolio, Blog…)"
                                                        value={socials.custom.label || ""}
                                                        onChange={(e) => updateSocialLabel(e.target.value)} />
                                                )}
                                            </>
                                        )}
                                    </div>
                                ))}

                                {/* Promo Video */}
                                <div style={s.divider} />
                                <div style={s.linkRow}>
                                    <div style={s.linkLeft}>
                                        <div style={{ ...s.linkIcon, background: "#fff0f0" }}>🎬</div>
                                        <div>
                                            <p style={s.linkLabel}>Promo Video</p>
                                            <p style={s.linkHint}>Play button on avatar — YouTube or upload</p>
                                        </div>
                                    </div>
                                    <label style={s.toggle}>
                                        <input type="checkbox" checked={promoVideoEnabled}
                                            onChange={() => setPromoVideoEnabled(v => !v)} style={{ display: "none" }} />
                                        <div style={{ ...s.toggleTrack, background: promoVideoEnabled ? "#ef4444" : "#e5e7eb" }}>
                                            <div style={{ ...s.toggleThumb, left: promoVideoEnabled ? "22px" : "2px" }} />
                                        </div>
                                    </label>
                                </div>
                                {promoVideoEnabled && (
                                    <>
                                        <div style={{ display: "flex", gap: "8px", margin: "8px 0" }}>
                                            <button
                                                type="button"
                                                onClick={() => setPromoVideoType("youtube")}
                                                style={{ flex: 1, padding: "8px", borderRadius: "8px", border: `2px solid ${promoVideoType === "youtube" ? "#ef4444" : "#e5e7eb"}`, background: promoVideoType === "youtube" ? "#fff0f0" : "#fff", color: promoVideoType === "youtube" ? "#ef4444" : "#6b7280", fontWeight: "600", cursor: "pointer", fontSize: "13px", fontFamily: "inherit" }}
                                            >▶ YouTube URL</button>
                                            <button
                                                type="button"
                                                onClick={() => setPromoVideoType("upload")}
                                                style={{ flex: 1, padding: "8px", borderRadius: "8px", border: `2px solid ${promoVideoType === "upload" ? "#ef4444" : "#e5e7eb"}`, background: promoVideoType === "upload" ? "#fff0f0" : "#fff", color: promoVideoType === "upload" ? "#ef4444" : "#6b7280", fontWeight: "600", cursor: "pointer", fontSize: "13px", fontFamily: "inherit" }}
                                            >📤 Upload Video</button>
                                        </div>
                                        {promoVideoType === "youtube" && (
                                            <input
                                                style={{ ...s.input, marginBottom: "0.5rem" }}
                                                placeholder="https://youtube.com/watch?v=..."
                                                value={promoVideoUrl}
                                                onChange={(e) => setPromoVideoUrl(e.target.value)}
                                            />
                                        )}
                                        {promoVideoType === "upload" && (
                                            <div style={s.resumeUploadArea}>
                                                <div style={{ fontSize: "1.5rem" }}>🎬</div>
                                                <div style={{ flex: 1 }}>
                                                    <p style={s.resumeTitle}>Video File</p>
                                                    <p style={s.resumeHint}>{promoVideoFile ? promoVideoFile.name : existingPromoVideoUrl ? "Video already uploaded" : "MP4, WebM — max 50MB"}</p>
                                                </div>
                                                <label htmlFor="promo-video-input" style={s.resumeBtn}>
                                                    {promoVideoFile || existingPromoVideoUrl ? "Change" : "Upload"}
                                                </label>
                                                <input id="promo-video-input" type="file" accept="video/mp4,video/webm,video/*"
                                                    ref={videoRef}
                                                    onChange={(e) => { const f = e.target.files[0]; if (f) setPromoVideoFile(f); }}
                                                    style={{ display: "none" }} />
                                            </div>
                                        )}
                                    </>
                                )}

                                {error && <p style={s.error}>{error}</p>}
                            </>
                        )}

                        {/* Navigation */}
                        <div style={s.navRow}>
                            {step > 0 && (
                                <button onClick={() => setStep(s => s - 1)} style={s.backStepBtn}>← Back</button>
                            )}
                            {step < STEPS.length - 1 ? (
                                <button onClick={() => setStep(s => s + 1)} style={s.nextBtn}>
                                    Continue →
                                </button>
                            ) : (
                                <button onClick={handleSubmit} style={s.nextBtn} disabled={loading}>
                                    {loading ? "Saving..." : "Save Changes ✓"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Right — Live Preview */}
            <div className="edit-preview-panel">
                <div style={s.previewInner}>
                    {/* Theme Picker */}
                    {/* Theme Picker Strip */}
                    <EditThemeStrip selected={theme} onSelect={setTheme} cardId={id} isPremium={isPremium} onUpgrade={() => setShowUpgrade(true)} />
                    {/* Full Card Preview */}
                    <style>{CARD_FONTS}</style>
                    <CardRenderer
                        themeId={theme}
                        cardData={{
                            name, phone, email, location, designation, company, bio,
                            photoPreview: displayPhoto,
                            bannerPreview: bannerPreview || existingBannerUrl,
                            website: websiteEnabled ? website : "",
                            resumeUrl: resumeName ? "preview" : existingResumeUrl,
                            promoVideoUrl: promoVideoEnabled ? (promoVideoUrl || existingPromoVideoUrl) : "",
                            promoVideoType,
                            socials: {
                                ...Object.fromEntries(
                                    SOCIAL_PLATFORMS.filter(p => socials[p.key]?.enabled && socials[p.key]?.url).map(p => [p.key, socials[p.key].url])
                                ),
                                ...(socials.custom?.enabled && socials.custom.label?.trim() ? { customLabel: socials.custom.label.trim() } : {}),
                            },
                        }}
                        onVideoClick={(promoVideoEnabled && (promoVideoUrl || existingPromoVideoUrl)) ? () => setShowVideoModal(true) : undefined}
                    />

                    <div style={{ paddingBottom: "32px" }} />
                </div>
            </div>

            {/* Mobile Tab Bar */}
            <div className="edit-tab-bar">
                <button
                    onClick={() => setMobileTab("form")}
                    style={{ flex: 1, border: "none", background: mobileTab === "form" ? "#f3f4ff" : "#fff", color: mobileTab === "form" ? "#6366f1" : "#9ca3af", fontWeight: "700", fontSize: "0.85rem", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "3px", borderTop: mobileTab === "form" ? "2px solid #6366f1" : "2px solid transparent", fontFamily: "inherit" }}
                >
                    <span style={{ fontSize: "1.1rem" }}>✏️</span> Edit
                </button>
                <button
                    onClick={() => setMobileTab("preview")}
                    style={{ flex: 1, border: "none", background: mobileTab === "preview" ? "#f3f4ff" : "#fff", color: mobileTab === "preview" ? "#6366f1" : "#9ca3af", fontWeight: "700", fontSize: "0.85rem", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "3px", borderTop: mobileTab === "preview" ? "2px solid #6366f1" : "2px solid transparent", fontFamily: "inherit" }}
                >
                    <span style={{ fontSize: "1.1rem" }}>👁️</span> Preview
                </button>
            </div>

            {/* ── Crop Modal ── */}
            {cropSrc && (
                <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
                    <div style={{ background: "#fff", borderRadius: "24px", padding: "24px 20px 20px", width: "100%", maxWidth: "320px", textAlign: "center" }}>
                        <h3 style={{ margin: "0 0 4px", fontSize: "16px", fontWeight: "700", color: "#111" }}>Crop Photo</h3>
                        <p style={{ margin: "0 0 16px", fontSize: "12px", color: "#9ca3af" }}>Drag to reposition · Scroll to zoom</p>

                        <div
                            style={{ width: "240px", height: "240px", borderRadius: "50%", overflow: "hidden", margin: "0 auto 16px", cursor: "grab", position: "relative", background: "#f3f4f6", boxShadow: "0 0 0 3px #0AADA1, 0 0 0 6px rgba(10,173,161,0.15)" }}
                            onPointerDown={(e) => {
                                e.currentTarget.setPointerCapture(e.pointerId);
                                cropDragRef.current = { startX: e.clientX, startY: e.clientY, startPos: { ...cropPos } };
                            }}
                            onPointerMove={(e) => {
                                if (!cropDragRef.current) return;
                                setCropPos({
                                    x: cropDragRef.current.startPos.x + (e.clientX - cropDragRef.current.startX),
                                    y: cropDragRef.current.startPos.y + (e.clientY - cropDragRef.current.startY),
                                });
                            }}
                            onPointerUp={() => { cropDragRef.current = null; }}
                            onWheel={(e) => {
                                e.preventDefault();
                                setCropZoom(z => Math.min(4, Math.max(0.1, z - e.deltaY * 0.003)));
                            }}
                        >
                            <img
                                ref={cropImgRef}
                                src={cropSrc}
                                alt="crop"
                                style={{ position: "absolute", top: "50%", left: "50%", transform: `translate(-50%, -50%) translate(${cropPos.x}px, ${cropPos.y}px) scale(${cropZoom})`, transformOrigin: "center", maxWidth: "none", userSelect: "none", pointerEvents: "none" }}
                                draggable={false}
                            />
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "0 12px 20px" }}>
                            <span style={{ fontSize: "11px", color: "#9ca3af" }}>–</span>
                            <input type="range" min="0.1" max="4" step="0.02" value={cropZoom}
                                onChange={e => setCropZoom(Number(e.target.value))}
                                style={{ flex: 1, accentColor: "#0AADA1" }}
                            />
                            <span style={{ fontSize: "13px", color: "#9ca3af" }}>+</span>
                        </div>

                        <div style={{ display: "flex", gap: "8px" }}>
                            <button onClick={() => setCropSrc(null)} style={{ flex: 1, padding: "12px", borderRadius: "12px", border: "1px solid #e5e7eb", background: "#f9fafb", fontSize: "14px", fontWeight: "600", cursor: "pointer", color: "#374151" }}>Cancel</button>
                            <button onClick={confirmCrop} style={{ flex: 1, padding: "12px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg,#0AADA1,#0f8f85)", color: "#fff", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>Crop & Use</button>
                        </div>
                    </div>
                </div>
            )}

            {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}

            {showVideoModal && (promoVideoUrl || existingPromoVideoUrl) && (
                <VideoModal
                    url={promoVideoUrl || existingPromoVideoUrl}
                    type={promoVideoType || "youtube"}
                    onClose={() => setShowVideoModal(false)}
                />
            )}
        </div>
    );
}

function VideoModal({ url, type, onClose }) {
    const ytId = type === "youtube" ? getYoutubeId(url) : null;
    return (
        <div
            style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
            onClick={onClose}
        >
            <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            <div style={{ width: "100%", maxWidth: "600px" }} onClick={e => e.stopPropagation()}>
                {ytId ? (
                    <div style={{ position: "relative", paddingBottom: "56.25%", borderRadius: "16px", overflow: "hidden", background: "#000" }}>
                        <iframe src={`https://www.youtube.com/embed/${ytId}?autoplay=1`} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }} allow="autoplay; encrypted-media; fullscreen" allowFullScreen title="Promo Video" />
                    </div>
                ) : (
                    <video src={url} controls autoPlay style={{ width: "100%", borderRadius: "16px", maxHeight: "80vh", background: "#000" }} />
                )}
            </div>
        </div>
    );
}

function getYoutubeId(url) {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
}

function Field({ label, value, onChange, placeholder, type = "text", required, span = 1 }) {
    return (
        <div style={{ gridColumn: `span ${span}` }}>
            <label style={s.label}>{label}</label>
            <input style={s.input} type={type} placeholder={placeholder}
                value={value} onChange={(e) => onChange(e.target.value)} required={required} />
        </div>
    );
}

function CameraIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
        </svg>
    );
}

function EditThemeStrip({ selected, onSelect, cardId, isPremium, onUpgrade }) {
    const navigate = useNavigate();

    const visibleThemes = (() => {
        const selectedTheme = THEMES.find(t => t.id === selected);
        const others = THEMES.filter(t => t.id !== selected);
        if (selectedTheme) return [selectedTheme, others[0]].filter(Boolean);
        return THEMES.slice(0, 2);
    })();

    const handleThemeClick = (t) => {
        const isFree = FREE_THEMES.includes(t.id);
        if (!isFree && !isPremium) { onUpgrade(); return; }
        onSelect(t.id);
    };

    return (
        <div style={{ marginBottom: "12px" }}>
            <div style={{ fontSize: "10px", fontWeight: "700", color: "rgba(255,255,255,0.4)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "8px" }}>Card Theme</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                {visibleThemes.map(t => {
                    const isFree = FREE_THEMES.includes(t.id);
                    const isSelected = selected === t.id;
                    const locked = !isFree && !isPremium;
                    return (
                        <div key={t.id} onClick={() => handleThemeClick(t)} style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "5px", opacity: locked ? 0.7 : 1 }}>
                            <div style={{ width: "100%", height: "48px", borderRadius: "8px", background: t.bannerBg || t.headerBg || t.topBand || t.accentBar || t.cardBg, border: isSelected ? "2px solid #0AADA1" : "2px solid transparent", boxShadow: isSelected ? "0 0 0 2px rgba(10,173,161,0.3)" : "none", position: "relative", overflow: "hidden", transition: "border 0.15s" }}>
                                <div style={{ position: "absolute", bottom: "5px", left: "6px", width: "20px", height: "20px", borderRadius: "50%", background: t.avatarBg || "#fff", border: `1.5px solid ${t.avatarBorder || t.accent || t.accentColor || "#fff"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "6px", fontWeight: "800", color: t.avatarColor || "#fff" }}>AM</div>
                                {locked && <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px" }}>🔒</div>}
                                {!isFree && !locked && <div style={{ position: "absolute", top: "4px", right: "5px", fontSize: "8px", color: "#f59e0b" }}>★</div>}
                            </div>
                            <div style={{ fontSize: "9px", fontWeight: "600", color: isSelected ? "#0AADA1" : "rgba(255,255,255,0.4)", textAlign: "center", lineHeight: 1.2, width: "100%" }}>{t.name.split(" ")[0]}</div>
                        </div>
                    );
                })}

                {/* "200+ more" tile */}
                <div
                    onClick={() => navigate("/themes", { state: { targetPath: `/edit/${cardId}`, confirmContext: "edit", preselectedTheme: selected } })}
                    style={{ cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}
                >
                    <div style={{ width: "100%", height: "48px", borderRadius: "8px", background: "rgba(10,173,161,0.08)", border: "2px dashed rgba(10,173,161,0.4)", display: "flex", alignItems: "center", justifyContent: "center", transition: "border 0.15s" }}>
                        <span style={{ fontSize: "16px", color: "#0AADA1", lineHeight: 1 }}>+</span>
                    </div>
                    <div style={{ fontSize: "9px", fontWeight: "600", color: "rgba(10,173,161,0.8)", textAlign: "center", lineHeight: 1.2, width: "100%" }}>200+ more</div>
                </div>
            </div>
        </div>
    );
}

const s = {
    page: {
        minHeight: "100vh", display: "grid",
        gridTemplateColumns: "1fr 420px",
        fontFamily: "'Segoe UI', Arial, sans-serif",
    },
    formPanel: {
        background: "#fff", display: "flex",
        flexDirection: "column", borderRight: "1px solid #f3f4f6", minHeight: "100vh",
    },
    topBar: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "1.25rem 2rem", borderBottom: "1px solid #f3f4f6",
    },
    backBtn: {
        background: "none", border: "none", color: "#6b7280",
        fontSize: "0.9rem", fontWeight: "600", cursor: "pointer", padding: 0,
    },
    topLogo: { fontSize: "1rem", fontWeight: "800", color: "#1a1a2e", display: "flex", alignItems: "center", gap: "5px" },
    logoDot: { color: "#6366f1", fontSize: "0.75rem" },

    stepBar: { display: "flex", alignItems: "flex-start", padding: "1.5rem 2rem", borderBottom: "1px solid #f3f4f6", gap: 0 },
    stepItem: { display: "flex", flexDirection: "column", alignItems: "center", position: "relative", flex: 1 },
    stepCircle: { width: "32px", height: "32px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", fontWeight: "800", transition: "all 0.3s", zIndex: 1 },
    stepLabel: { fontSize: "0.7rem", marginTop: "6px", textAlign: "center", transition: "all 0.3s", whiteSpace: "nowrap" },
    stepLine: { position: "absolute", top: "16px", left: "calc(50% + 16px)", width: "calc(100% - 32px)", height: "2px", transition: "background 0.3s" },

    formBody: { flex: 1, padding: "2rem", overflowY: "auto" },
    stepContent: { maxWidth: "560px", margin: "0 auto" },
    stepHeading: { fontSize: "1.5rem", fontWeight: "900", color: "#1a1a2e", margin: "0 0 0.4rem" },
    stepSub: { fontSize: "0.9rem", color: "#6b7280", margin: "0 0 2rem", lineHeight: 1.6 },

    photoArea: { display: "flex", alignItems: "center", gap: "1.5rem", background: "#f8f9ff", borderRadius: "16px", padding: "1.25rem 1.5rem", marginBottom: "1.5rem", border: "1.5px solid #e5e7eb" },
    avatarWrap: { position: "relative", width: "72px", height: "72px", flexShrink: 0 },
    avatarImg: { width: "72px", height: "72px", borderRadius: "50%", objectFit: "cover", border: "3px solid #e5e7eb" },
    avatarInitials: { width: "72px", height: "72px", borderRadius: "50%", background: "linear-gradient(135deg, #1a1a2e, #0f3460)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem", fontWeight: "700" },
    cameraBtn: { position: "absolute", bottom: "0px", right: "0px", width: "26px", height: "26px", borderRadius: "50%", background: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 6px rgba(99,102,241,0.4)" },
    photoTitle: { fontWeight: "700", fontSize: "0.9rem", color: "#1a1a2e", margin: "0 0 0.2rem" },
    photoHint: { fontSize: "0.78rem", color: "#9ca3af", margin: "0 0 0.6rem" },
    photoUploadBtn: { display: "inline-block", padding: "0.4rem 1rem", background: "#f3f4f6", border: "1.5px solid #e5e7eb", borderRadius: "8px", fontSize: "0.82rem", fontWeight: "600", color: "#374151", cursor: "pointer" },

    fieldGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" },
    field: { display: "flex", flexDirection: "column", gap: "0.35rem", marginBottom: "1rem" },
    label: { fontSize: "0.8rem", fontWeight: "700", color: "#374151", marginBottom: "0.35rem", display: "block" },
    input: { padding: "0.75rem 1rem", border: "1.5px solid #e5e7eb", borderRadius: "10px", fontSize: "0.92rem", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" },

    resumeUploadArea: { display: "flex", alignItems: "center", gap: "1rem", background: "#f8f9ff", borderRadius: "14px", padding: "1rem 1.25rem", border: "1.5px dashed #d1d5db", marginTop: "1rem" },
    resumeTitle: { fontWeight: "700", fontSize: "0.88rem", color: "#1a1a2e", margin: "0 0 0.2rem" },
    resumeHint: { fontSize: "0.78rem", color: "#9ca3af", margin: 0 },
    resumeBtn: { padding: "0.5rem 1rem", background: "#6366f1", color: "#fff", border: "none", borderRadius: "8px", fontSize: "0.82rem", fontWeight: "700", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" },
    resumeDeleteBtn: { padding: "0.4rem 0.65rem", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: "8px", fontSize: "0.85rem", fontWeight: "700", cursor: "pointer", flexShrink: 0, lineHeight: 1 },

    linkRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.75rem 0" },
    linkLeft: { display: "flex", alignItems: "center", gap: "0.75rem" },
    linkIcon: { width: "38px", height: "38px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem" },
    socialBadge: { width: "38px", height: "38px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" },
    linkLabel: { fontWeight: "700", fontSize: "0.9rem", color: "#1a1a2e", margin: 0 },
    linkHint: { fontSize: "0.75rem", color: "#9ca3af", margin: 0 },
    toggle: { cursor: "pointer", position: "relative" },
    toggleTrack: { width: "44px", height: "24px", borderRadius: "50px", position: "relative", transition: "background 0.25s" },
    toggleThumb: { width: "20px", height: "20px", borderRadius: "50%", background: "#fff", position: "absolute", top: "2px", transition: "left 0.25s", boxShadow: "0 1px 4px rgba(0,0,0,0.2)" },
    divider: { height: "1px", background: "#f3f4f6", margin: "0.5rem 0" },

    navRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "2rem", paddingTop: "1.5rem", borderTop: "1px solid #f3f4f6" },
    backStepBtn: { background: "none", border: "1.5px solid #e5e7eb", borderRadius: "10px", padding: "0.75rem 1.5rem", fontSize: "0.9rem", fontWeight: "600", color: "#6b7280", cursor: "pointer" },
    nextBtn: { marginLeft: "auto", padding: "0.75rem 2rem", background: "linear-gradient(135deg, #6366f1, #4f46e5)", color: "#fff", border: "none", borderRadius: "10px", fontSize: "0.95rem", fontWeight: "700", cursor: "pointer", boxShadow: "0 4px 16px rgba(99,102,241,0.3)" },
    error: { color: "#ef4444", fontSize: "0.875rem", margin: "0 0 1rem", textAlign: "center" },

    // PREVIEW
    previewPanel: { background: "linear-gradient(160deg, #0f0c29 0%, #1a1a2e 50%, #0f3460 100%)", padding: "2rem 1.5rem", display: "flex", flexDirection: "column", alignItems: "center", position: "sticky", top: 0, height: "100vh", overflowY: "auto" },
    previewInner: { width: "100%", maxWidth: "420px" },
    tabRow: { display: "flex", gap: "0.5rem", marginBottom: "1rem", background: "rgba(255,255,255,0.08)", borderRadius: "12px", padding: "4px" },
    tabBtn: { flex: 1, padding: "0.5rem", background: "transparent", border: "none", borderRadius: "9px", fontSize: "0.78rem", fontWeight: "600", color: "rgba(255,255,255,0.5)", cursor: "pointer", transition: "all 0.2s" },
    tabBtnActive: { background: "#fff", color: "#1a1a2e", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" },

    previewCard: { background: "#fff", borderRadius: "20px", overflow: "hidden", boxShadow: "0 30px 60px rgba(0,0,0,0.5)", marginBottom: "1rem" },
    previewHeader: { background: "linear-gradient(135deg, #1a1a2e, #0f3460)", padding: "1.5rem 1.25rem 1.25rem", textAlign: "center" },
    previewAvatarWrap: { marginBottom: "0.75rem" },
    previewAvatarImg: { width: "56px", height: "56px", borderRadius: "50%", objectFit: "cover", border: "3px solid rgba(255,255,255,0.3)", display: "block", margin: "0 auto" },
    previewAvatarInitials: { width: "56px", height: "56px", borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "3px solid rgba(255,255,255,0.3)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", fontWeight: "800", margin: "0 auto" },
    previewName: { color: "#fff", fontSize: "1rem", fontWeight: "800", margin: "0 0 0.25rem" },
    previewRole: { color: "rgba(255,255,255,0.75)", fontSize: "0.72rem", margin: "0 0 0.2rem" },
    previewLocation: { color: "rgba(255,255,255,0.55)", fontSize: "0.68rem", margin: 0 },
    previewSection: { padding: "0.75rem 1rem", borderBottom: "1px solid #f3f4f6" },
    previewRow: { display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.75rem", color: "#374151", padding: "0.2rem 0" },
    previewBio: { padding: "0.75rem 1rem", borderBottom: "1px solid #f3f4f6" },
    previewBioText: { fontSize: "0.72rem", color: "#6b7280", margin: 0, lineHeight: 1.5 },

    accessDenied: { minHeight: "100vh", background: "linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #0f3460 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", fontFamily: "'Segoe UI', Arial, sans-serif" },
    accessBox: { background: "#fff", borderRadius: "24px", padding: "3rem 2.5rem", width: "100%", maxWidth: "400px", textAlign: "center", boxShadow: "0 40px 80px rgba(0,0,0,0.5)" },
    accessIcon: { fontSize: "3rem", marginBottom: "1rem" },
    accessTitle: { fontSize: "1.5rem", fontWeight: "900", color: "#1a1a2e", margin: "0 0 0.75rem" },
    accessSub: { fontSize: "0.9rem", color: "#6b7280", margin: "0 0 2rem", lineHeight: 1.6 },
    accessBtn: { padding: "0.85rem 2rem", background: "linear-gradient(135deg, #6366f1, #4f46e5)", color: "#fff", border: "none", borderRadius: "12px", fontSize: "0.95rem", fontWeight: "700", cursor: "pointer" },

    slugWrap: { display: "flex", alignItems: "center", border: "1.5px solid #e5e7eb", borderRadius: "10px", overflow: "hidden" },
    slugBase: { background: "#f3f4f6", padding: "0.75rem 0.75rem", fontSize: "0.82rem", color: "#6b7280", fontWeight: "600", whiteSpace: "nowrap", borderRight: "1.5px solid #e5e7eb" },
    slugInput: { flex: 1, padding: "0.75rem 0.75rem", border: "none", outline: "none", fontSize: "0.92rem", fontFamily: "inherit" },
    slugStatus: { marginTop: "0.4rem", fontSize: "0.78rem", lineHeight: 1.5 },
    slugAvailable: { color: "#16a34a" },
    slugTaken: { color: "#dc2626" },
    slugChecking: { color: "#9ca3af" },

};
